import React, { useState } from 'react';
import Modal from './Modal';
import './SkiGroupJoinPrompt.css';

/**
 * Asked when a simulation starts and a live agent is already in a ski group: should the
 * virtual agents join that group for the run?
 *
 * The live user owns the group — they create it and join it in the app — so this only ever
 * moves virtual agents in. Only live agents that are actually in a group are offered, and the
 * first one is selected, which is the common case of a single live skier.
 *
 * Props:
 *   isOpen
 *   candidates: [{ uid, name, groupId, groupName }] live agents that are in a group
 *   agentCount: how many virtual agents would move
 *   onConfirm(groupId, liveUid) — liveUid is the skier the agents then follow
 *   onSkip()
 */
const SkiGroupJoinPrompt = ({ isOpen, candidates = [], agentCount = 0, onConfirm, onSkip }) => {
  const [selectedUid, setSelectedUid] = useState(null);
  if (!isOpen || candidates.length === 0) return null;

  const selected = candidates.find((candidate) => candidate.uid === selectedUid) || candidates[0];
  const agentLabel = `${agentCount} virtual ${agentCount === 1 ? 'agent' : 'agents'}`;

  return (
    <Modal isOpen={isOpen} onClose={onSkip} title="Join the live skier's ski group?" className="ski-group-join-prompt">
      <p className="ski-group-join-intro">
        {candidates.length === 1
          ? `${selected.name} is in ${selected.groupName || 'a ski group'}. Move ${agentLabel} into it for this run?`
          : `Several live skiers are in a ski group. Pick whose group ${agentLabel} should join.`}
      </p>

      {candidates.length > 1 && (
        <div className="ski-group-join-options" role="radiogroup" aria-label="Live skier">
          {candidates.map((candidate) => (
            <label key={candidate.uid} className={`ski-group-join-option ${selected.uid === candidate.uid ? 'selected' : ''}`}>
              <input
                type="radio"
                name="ski-group-join-candidate"
                checked={selected.uid === candidate.uid}
                onChange={() => setSelectedUid(candidate.uid)}
              />
              <span className="ski-group-join-option-text">
                <span className="ski-group-join-option-name">{candidate.name}</span>
                <span className="ski-group-join-option-group">{candidate.groupName || candidate.groupId}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      <p className="ski-group-join-note">
        The agents stay in the group after the simulation ends, the same as a real skier would.
      </p>

      <div className="ski-group-join-actions">
        <button type="button" className="ski-group-join-skip" onClick={onSkip}>
          Start without joining
        </button>
        <button type="button" className="ski-group-join-confirm" onClick={() => onConfirm(selected.groupId, selected.uid)}>
          Join and start
        </button>
      </div>
    </Modal>
  );
};

export default SkiGroupJoinPrompt;
